import {
    createContext,
    Dispatch,
    ReactNode,
    SetStateAction,
    useContext,
    useMemo,
    useState,
} from 'react';
import { TUser } from './UsersProvider';
import { useSessionStart } from './AuthProvider';
import { useProfileOperations } from '../hooks/useProfileOperations';
import { useLanguage } from '../hooks/useLanguage';

type TProps = {
    children: ReactNode;
};

type TContext = {
    profile?: TUser;
    alertMsgs: Partial<Record<string, string[]>>;
    qrCode: string;
    closeUpdateModal: boolean;
    loading?: boolean;
    setLoading: Dispatch<SetStateAction<boolean>>;
    setCloseUpdateModal: Dispatch<SetStateAction<boolean>>;
    removeQr: () => Promise<void>;
    getQR: () => Promise<void>;
    add2FaSecure: (totp_token: string) => Promise<void>;
    changeLanguage: (lang: string) => void;
    updateFullName: (params: { firstName: string; lastName: string }) => void;
    updatePassword: (params: { password: string; confirmPassword: string }) => void;
    updatePhoneNumber: (phone: string) => void;
};

const DashboardContext = createContext<TContext>({} as TContext);

export const DashboardProvider = ({ children }: TProps) => {
    const [closeUpdateModal, setCloseUpdateModal] = useState(false);
    const [alertMsgs, setAlertMessages] = useState<Partial<Record<string, string[]>>>({});

    const sessionStart = useSessionStart();
    const {
        profile,
        loading,
        qrCode,
        setProfile,
        setLoading,
        getProfile,
        updateFullName: updateName,
        updatePhoneNumber: updatePhone,
        updatePassword: updatePass,
        getQR,
        add2FaSecure,
        removeQr,
    } = useProfileOperations({
        setProfile: undefined,
        setLoading: undefined,
        setCloseUpdateModal,
        sessionStart,
    });

    const { changeLanguage: changeLang } = useLanguage(setProfile, setLoading, getProfile);

    const updateFullName = async ({ firstName, lastName }: { firstName: string; lastName: string }) => {
        await updateName(firstName, lastName, setAlertMessages);
    };

    const updatePhoneNumber = async (phone: string) => {
        await updatePhone(phone, setAlertMessages);
    };

    const updatePassword = async ({ password, confirmPassword }: { password: string; confirmPassword: string }) => {
        await updatePass(password, confirmPassword, setAlertMessages);
    };

    const changeLanguage = async (lang: string) => {
        await changeLang(lang, setAlertMessages);
    };

    const removeQrHandler = async () => {
        await removeQr(setAlertMessages);
    };

    const getQRHandler = async () => {
        await getQR(setAlertMessages);
    };

    const add2FaSecureHandler = async (totp_token: string) => {
        await add2FaSecure(totp_token, setAlertMessages);
    };

    const contextValue = useMemo(
        () => ({
            profile,
            alertMsgs,
            qrCode,
            loading,
            closeUpdateModal,
            setLoading,
            setCloseUpdateModal,
            add2FaSecure: add2FaSecureHandler,
            updateFullName,
            updatePhoneNumber,
            updatePassword,
            changeLanguage,
            getQR: getQRHandler,
            removeQr: removeQrHandler,
        }),
        [profile, alertMsgs, qrCode, loading, closeUpdateModal]
    );

    return (
        <DashboardContext.Provider value={contextValue}>
            {children}
        </DashboardContext.Provider>
    );
};

export const useDashboard = (): TContext => {
    return useContext(DashboardContext);
};
